import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { Hero } from "@/components/layout/sections/Hero";
import ImageSection from "@/components/layout/sections/ImageSection";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faArrowDownToLine, faArrowRightArrowLeft, faCode, faImages, faUpload } from "@icons/regular";
import { Button, Icon } from "@ui";
import { Link, graphql } from "gatsby";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const gridFeatures = [
  {
    id: 1,
    title: "Preview before publishing",
    description: "See how your story displays on desktop, mobile, and tablet — so you can be confident it looks great everywhere.",
    imageUrl: "/product/publish-and-host/preview-before-publishing.png",
    imageAlt: "A screenshot of collaborative editing in the Shorthand editor",
  },
  {
    id: 2,
    title: "One-click publishing",
    description: "Get your content live faster. Ready, set, publish, and republish your Shorthand story with a single click.",
    imageUrl: "/product/publish-and-host/one-click-publishing.png",
    imageAlt: "A logo of Shorthand one-click publishing",
  },
  {
    id: 3,
    title: "Flexible visibility settings",
    description:
      "Confidently control access to your stories. Publish to a private hidden link, or invite viewers with a one-time password to a protected story.",
    imageUrl: "/product/publish-and-host/flexible-visibility-settings.png",
    imageAlt: "A screenshot of flexible visibility settings in the Shorthand editor",
  },
  {
    id: 4,
    title: "Scheduled publishing",
    description:
      "Publish at the right moment. No need to stay up late or wake up early— schedule your story to publish whenever you want.",
    imageUrl: "/product/publish-and-host/scheduled-publishing.png",
    imageAlt: "A screenshot of scheduled publishing in the Shorthand editor",
  },
];

const iconGrid = [
  {
    id: 1,
    icon: undefined,
    title: "Host with Shorthand",
    description:
      "Let Shorthand host, and publish to your chosen domain or subdomain. Backed by Amazon S3 and CloudFront, your stories are delivered at speed and scale.",
  },
  {
    id: 2,
    icon: faUpload,
    title: "Self-hosting",
    description:
      "Publish via your own S3 bucket, API, or custom infrastructure while still using Shorthand’s powerful storytelling tools.",
  },
  {
    id: 3,
    icon: faArrowDownToLine,
    title: "Full story export",
    description:
      "Download your story as a zip archive — including code, media, and a full JSON representation of your story — and host it wherever you like.",
  },
  {
    id: 4,
    icon: faCode,
    title: "Story embedding",
    description:
      "Embed Shorthand-hosted stories into any webpage with ease. Simply copy and paste the automatically generated code snippet to integrate your content seamlessly.",
  },
  {
    id: 5,
    icon: faImages,
    title: "Asset server",
    description:
      "Host your images and videos separately from your story HTML to boost performance, enhance security, and keep your content loading fast.",
  },
  {
    id: 6,
    icon: faArrowRightArrowLeft,
    title: "CMS plugins",
    description:
      "Integrate Shorthand with your CMS using our WordPress or Drupal plugins, or the Story API, to publish and host directly on your platform.",
  },
];

const FeaturesPublishingAndHosting: React.FC<{ location: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>
        <Hero
          title={<>Publish and host, hassle free</>}
          description={
            <>
              Publishing should be as effortless as creating. Shorthand gives you unmatched flexibility to host and deliver stunning,
              high-performance content your way.
            </>
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />

        <Section>
          <TitleSection
            align="left"
            title="All set to publish"
            description="With full control of your publishing process, you’ll have the speed and confidence to get your stories out there."
          />
          <div className="tw-mx-auto tw-mt-10 tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-mt-16">
            {gridFeatures.map(feature => (
              <div className="tw-relative" key={`pub-grid-feature-${feature.id}`}>
                <div className="tw-absolute tw-inset-px tw-rounded-lg tw-bg-grey_scale-800/50" />
                <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[calc(theme(borderRadius.lg)+1px)]">
                  <div className="tw-px-4 tw-pb-1 tw-pt-4 sm:tw-px-10 sm:tw-pb-0 sm:tw-pt-10">
                    <p className="tw-mt-2 !tw-text-lg tw-font-medium tw-tracking-tight tw-text-[var(--foreground-color)] default-max-lg:tw-text-center">
                      {feature.title}
                    </p>
                    <p className="tw-mt-2 tw-max-w-lg !tw-text-base tw-text-[var(--foreground-color-muted)] default-max-lg:tw-text-center">
                      {feature.description}
                    </p>
                  </div>
                  <div className="tw-flex-grow"></div>
                  <div className="tw-relative tw-mt-4">
                    <div className="tw-relative tw-ml-4 tw-mr-0 tw-overflow-hidden tw-rounded-tl-xl tw-bg-grey_scale-800/50 tw-shadow-2xl tw-outline tw-outline-white/10 sm:tw-ml-10">
                      <div className="tw-relative tw-aspect-video">
                        <img
                          alt={feature.imageAlt}
                          src={feature.imageUrl}
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-pointer-events-none tw-absolute tw-inset-px tw-rounded-lg tw-shadow tw-outline tw-outline-black/5" />
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <TitleSection
            align="left"
            title="Secure, high-performance hosting"
            description={
              <>
                Choose the publishing setup that fits your team — from full control on your servers to the ease of Shorthand hosting.
              </>
            }
          />
          <div className="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-8 sm:tw-grid-cols-3">
            {iconGrid.map(feature => (
              <div
                className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-grey_scale-800/50 tw-p-8"
                key={`preview-feature-${feature.id}`}
              >
                <div className="tw-h-12 tw-w-12">
                  {feature.title === "Host with Shorthand" && <img src="/media/logos/shorthand-logo-rev.svg" alt="Shorthand logo" />}
                  {feature.icon && <Icon icon={feature.icon} className="tw-h-full tw-w-full tw-text-white" />}
                </div>
                <h3 className="!tw-text-lg tw-font-semibold tw-tracking-tight tw-text-brand_scale-400">{feature.title}</h3>
                <span className="tw-max-w-[30ch] tw-text-base/6 tw-text-[var(--foreground-color)]">{feature.description}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                <span className="tw-font-PassengerSerif tw-italic">From idea to publish</span>
              </>
            }
            description="Bring your vision to life with speed and style — and share it in a way that engages, inspires, and delivers results."
          />
          <ImageSection
            image="product/publish-and-host/from-idea-to-publish.png"
            imageAlt="Editor screenshot showing the story preview with mobile view selected"
          />
        </Section>

        <ProductFooter />
        <Footer className="mt-0" />
      </main>
    </Layout>
  );
};

export default FeaturesPublishingAndHosting;

export const query = graphql`
  query FeaturesPublishingAndHostingQuery {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
  }
`;
